import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import type { ArticleData } from '@/mocks/articles';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://vigorbuyspread.store';

function toSlug(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

export default function ArticleTemplate({ article }: { article: ArticleData }) {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    author: { '@type': 'Organization', name: 'Vigorbuy Spreadsheet' },
    datePublished: article.datePublished,
    dateModified: article.datePublished,
    description: article.metaDescription,
    publisher: { '@type': 'Organization', name: 'Vigorbuy Spreadsheet' },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${siteUrl}/` },
      { '@type': 'ListItem', position: 2, name: article.title, item: `${siteUrl}/${article.slug}` },
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: article.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: { '@type': 'Answer', text: faq.answer },
    })),
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <Navbar />
      <main className="pt-20 md:pt-24">
        <article className="max-w-4xl mx-auto px-4 md:px-6 py-10 md:py-16">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-8 flex-wrap" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">Home</Link>
            <span className="w-3 h-3 flex items-center justify-center"><i className="ri-arrow-right-s-line text-xs"></i></span>
            <span className="text-foreground-700 font-medium">{article.badge}</span>
          </nav>

          {/* Header */}
          <header className="mb-10">
            <span className="inline-block px-3 py-1 rounded-full bg-accent-100/80 text-accent-700 text-xs font-medium mb-4 whitespace-nowrap">
              {article.badge}
            </span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight mb-4">
              {article.title}
            </h1>
            <p className="text-foreground-600 text-sm md:text-base leading-relaxed max-w-3xl">
              {article.metaDescription}
            </p>
            <div className="flex flex-wrap items-center gap-4 mt-6 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-calendar-line text-xs"></i></span>
                Updated: {article.datePublished}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-time-line text-xs"></i></span>
                {article.readTime}
              </span>
            </div>
          </header>

          {/* Featured Image */}
          <div className="relative w-full h-[300px] md:h-[420px] rounded-2xl overflow-hidden mb-10">
            <img src={article.imageUrl} alt={article.imageAlt} className="w-full h-full object-cover object-top" />
          </div>

          {/* Table of Contents */}
          <nav className="bg-background-100 border border-background-200/70 rounded-xl p-5 md:p-6 mb-10" aria-label="Table of Contents">
            <h2 className="font-heading text-lg font-semibold text-foreground-900 mb-4">Table of Contents</h2>
            <ol className="space-y-2">
              {article.sections.map((section, idx) => (
                <li key={section.id} className="flex items-start gap-3">
                  <span className="text-primary-500 font-medium text-sm mt-0.5">{idx + 1}.</span>
                  <a href={`#${section.id}`} className="text-foreground-700 hover:text-primary-500 text-sm transition-colors cursor-pointer">{section.title}</a>
                </li>
              ))}
            </ol>
          </nav>

          {/* Article Sections */}
          {article.sections.map((section) => (
            <section key={section.id} id={section.id} className="mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">{section.title}</h2>
              <div className="prose-custom space-y-4 text-foreground-700 leading-relaxed text-sm md:text-base">
                {section.paragraphs.map((p, i) => (
                  <p key={i} dangerouslySetInnerHTML={{ __html: p }}></p>
                ))}
              </div>
            </section>
          ))}

          {/* FAQ Section */}
          <section className="mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
              Frequently Asked Questions
            </h2>
            <div className="prose-custom space-y-4 text-foreground-700 leading-relaxed text-sm md:text-base">
              {article.faqs.map((faq, idx) => (
                <div key={idx} className="bg-background-100 rounded-xl p-5">
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">{faq.question}</h3>
                  <p>{faq.answer}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Internal Link CTA */}
          <div className="bg-gradient-to-r from-primary-500 to-primary-600 rounded-2xl p-6 md:p-8 text-center">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-background-50 mb-3">
              Ready to Start Discovering?
            </h2>
            <p className="text-background-50/80 text-sm mb-5 max-w-md mx-auto">
              Return to the homepage and explore all 14 product categories with Vigorbuy Spreadsheet.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link to="/" className="inline-flex items-center justify-center gap-2.5 px-7 py-3 bg-background-50 text-primary-600 rounded-full text-sm font-semibold hover:bg-background-100 transition-all duration-200 shadow-lg whitespace-nowrap cursor-pointer">
                <span>Back to Homepage</span>
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-home-4-line text-base"></i></span>
              </Link>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="inline-flex items-center justify-center gap-2.5 px-7 py-3 bg-background-50/15 backdrop-blur-sm border border-background-50/30 text-background-50 rounded-full text-sm font-semibold hover:bg-background-50/25 transition-all duration-200 whitespace-nowrap cursor-pointer">
                <span>Visit Main Website</span>
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-up-line text-base"></i></span>
              </a>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}